/**
 * cmlStatus — single source of truth for the 5-level CML workflow status →
 * color + display label mapping (Article III / D16).
 *
 * Per SC-002, the 3D marker pill, the Wave C-α popup card pill, and the Wave
 * C-δ Monitoring tab row pill MUST render the same color for the same status
 * value. Before this helper existed, the mapping was duplicated across
 * `CmlPopupCard.vue` and `useMonitoringTab.js`; a drift between the two would
 * silently violate SC-002 (popup says yellow, panel says orange — Article III
 * vocabulary preserved at the enum level but visualization diverges).
 *
 * The helper centralizes the maps so any future status-color decision (e.g.,
 * adding `WaitingRepair` as a new enum value, or shifting `Monitor` to a
 * different yellow shade for accessibility) flows to all consumers atomically.
 *
 * Consumers (Wave C-δ inventory):
 *   - src/components/cml/CmlPopupCard.vue (Wave C-α popup pill)
 *   - src/features/Tags/components/monitoring/CmlRecordRow.vue (via re-export
 *     from useMonitoringTab.js — preserves the slice-B import surface so the
 *     row component doesn't need to change paths)
 *   - The 3D marker pipeline (forgeService) — TODO: still consumes its own
 *     legacy 3-level color function; aligning marker to this 5-level source
 *     is a Phase 1 §11 follow-up (SC-006 in 003 spec). T043 extracts the
 *     shared helper but does not yet rewire the marker pipeline.
 */

/**
 * 5-level workflow status enum → CSS hex color.
 *
 * Returns the grey fallback for null / undefined / unknown statuses. This
 * matches the original CmlPopupCard.vue behavior (fallback `#9E9E9E`).
 *
 * @param {string|null|undefined} status
 * @returns {string} hex color (e.g., '#4CAF50')
 */
export function statusColorForCml(status) {
  const colors = {
    Acceptable: '#4CAF50',     // green — within Acceptable Corrosion Rate
    Monitor: '#FFEB3B',        // yellow — flagged for monitoring
    ReInspect: '#FF9800',      // orange — re-inspection required
    FfsRequired: '#9C27B0',    // purple — Fitness-For-Service evaluation
    RepairRequired: '#F44336', // red — repair required
  };
  return colors[status] || '#9E9E9E';
}

/**
 * 5-level workflow status enum → short display label.
 *
 * Returns the FR-018 placeholder ('—') for null / undefined status. Returns
 * the raw enum value as a defensive fallback for unknown enum members (so a
 * future enum addition still renders SOMETHING readable, just unprettified).
 *
 * @param {string|null|undefined} status
 * @returns {string}
 */
export function statusLabelForCml(status) {
  if (!status) return '—';
  const labels = {
    Acceptable: 'Acceptable',
    Monitor: 'Monitor',
    ReInspect: 'Re-inspect',
    FfsRequired: 'FFS',
    RepairRequired: 'Repair',
  };
  return labels[status] || status;
}
